import type { CreativeCarousel } from './creative-api';

/** Sub-tabs within Website Creatives: what the home page shows above the fold, the pool, or a curated carousel. */
export type SubTab = 'above-the-fold' | 'pool' | CreativeCarousel;

/** Every sub-tab, in display order: above the fold first, then the pool (the library), then the carousels. */
export const SUB_TABS: SubTab[] = ['above-the-fold', 'pool', 'homepage', 'casino', 'sports'];

const isSubTab = (value: string | null): value is SubTab => value !== null && SUB_TABS.some((tab) => tab === value);

/**
 * The sub-tab Website Creatives opens on: the `view` parameter when it names one (`?view=casino`, the Website hub's
 * deep link), else the pool. Read once, as the initial state; the parameter stays in the URL, so a reload opens the
 * same view.
 */
export const initialSubTab = (search: string): SubTab => {
  const view = new URLSearchParams(search).get('view');
  return isSubTab(view) ? view : 'pool';
};

/**
 * The sub-tabs on screen, in display order. Above the fold shows only while the brand's home page may have something
 * there (`aboveTheFold`: its read is in flight, failed, or found something); the carousels only behind their gate.
 * The pool always shows. Pure.
 */
export const visibleSubTabsOf = (aboveTheFold: boolean, carouselsEnabled: boolean): SubTab[] =>
  SUB_TABS.filter((tab) => {
    if (tab === 'above-the-fold') return aboveTheFold;
    if (tab === 'pool') return true;
    return carouselsEnabled;
  });

/** The sub-tab on screen: the chosen one while it shows, else the pool. Pure. */
export const effectiveSubTabOf = (chosen: SubTab, visible: SubTab[]): SubTab => (visible.includes(chosen) ? chosen : 'pool');
